import Column from '@app/components/layout/Column'
import { Select } from '@app/components/core/input/select/Select'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { NUMBER_OF_HOMES, NumberOfHomes } from '@schema/initiative'
import { FieldEditActions } from '../shared/FieldEditActions'
import { useEditContainerClose } from '../shared/useEditContainerClose'

export type NumberOfHomesFieldEditProps = {
  initiativeId: number
  numberOfHomes: NumberOfHomes | null
}

export const NumberOfHomesFieldEdit = ({
  initiativeId,
  numberOfHomes,
}: NumberOfHomesFieldEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('NumberOfHomesFieldEdit requires an EditContainer ancestor')
  }
  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const t = useTranslations().app.common
  const creationTranslations =
    useTranslations().app.dashboard.initiative.creation
  const emptyLabel = useTranslations().app.common.notSpecified

  const [value, setValue] = useState<NumberOfHomes | null>(numberOfHomes)

  useEffect(() => {
    setValue(numberOfHomes)
  }, [numberOfHomes])

  const save = useCallback(() => {
    void client
      .call('edit-initiative', {
        id: initiativeId,
        value: {
          numberOfHomes: value ?? undefined,
        },
      })
      .then((success) => {
        if (success) {
          showSuccessToast(t.toastSuccess)
          close()
        } else {
          showFailToast()
        }
      })
      .catch(() => showFailToast())
  }, [
    client,
    initiativeId,
    value,
    close,
    showFailToast,
    showSuccessToast,
    t.toastSuccess,
  ])

  return (
    <Column gap="1rem" alignItems="stretch">
      <Select
        label={creationTranslations.numberOfHomes}
        value={value}
        setValue={setValue}
        notSelectedLabel={emptyLabel}
        options={Object.values(NUMBER_OF_HOMES).map((item) => ({
          value: item,
          label: creationTranslations.numberOfHomesOptions[item] ?? emptyLabel,
        }))}
      />
      <FieldEditActions onCancel={close} onSave={save} />
    </Column>
  )
}
